/**
 * Insights Filter & Pagination
 *
 * Client-side filtering and pagination for the insights page.
 * Filters insight cards based on their service tags and paginates results.
 */

(function () {
  // Data attribute selectors (separate from styling)
  const FILTER_BUTTON_SELECTOR = '[data-insights-filter-button]';
  const INSIGHT_ITEM_SELECTOR = '[data-insights-item]';
  const STATUS_SELECTOR = '[data-insights-filter-status]';
  const PAGINATION_CONTAINER_SELECTOR = '[data-insights-pagination]';
  const PAGINATION_LIST_SELECTOR = '[data-insights-pagination-list]';
  const PAGINATION_INDICATOR_SELECTOR = '[data-insights-pagination-indicator]';
  const PAGINATION_BUTTON_SELECTOR = '[data-insights-pagination-button]';
  const INSIGHTS_SECTION_SELECTOR = '[data-insights-section]';

  // State classes (used for styling hooks)
  const ACTIVE_CLASS = 'insights-filter__button--active';
  const HIDDEN_CLASS = 'insights-item--hidden';
  const PAGINATED_HIDDEN_CLASS = 'insights-item--paginated-hidden';
  const PAGINATION_ACTIVE_CLASS = 'insights-pagination__button--active';

  const ITEMS_PER_PAGE = 9;

  /** @type {number} */
  let currentPage = 1;
  /** @type {string} */
  let currentFilter = 'all';
  /** @type {HTMLElement[]} */
  let filteredItems = [];

  /**
   * Initialize the insights filter and pagination functionality
   */
  function init() {
    /** @type {NodeListOf<HTMLElement>} */
    const filterButtons = document.querySelectorAll(FILTER_BUTTON_SELECTOR);
    /** @type {NodeListOf<HTMLElement>} */
    const insightItems = document.querySelectorAll(INSIGHT_ITEM_SELECTOR);
    /** @type {HTMLElement | null} */
    const statusElement = document.querySelector(STATUS_SELECTOR);
    /** @type {HTMLElement | null} */
    const paginationContainer = document.querySelector(PAGINATION_CONTAINER_SELECTOR);

    if (!insightItems.length) return;

    // Add click and keyboard listeners to all filter buttons
    if (filterButtons.length) {
      filterButtons.forEach((button, index) => {
        button.addEventListener('click', () => {
          currentPage = 1; // Reset to page 1 when filter changes
          handleFilterClick(button, filterButtons, insightItems, statusElement, paginationContainer);
        });

        // Keyboard navigation for tablist pattern (WAI-ARIA)
        button.addEventListener('keydown', (event) => {
          const buttonsArray = Array.from(filterButtons);
          let targetIndex = -1;

          switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
              event.preventDefault();
              targetIndex = index > 0 ? index - 1 : buttonsArray.length - 1;
              break;
            case 'ArrowRight':
            case 'ArrowDown':
              event.preventDefault();
              targetIndex = index < buttonsArray.length - 1 ? index + 1 : 0;
              break;
            case 'Home':
              event.preventDefault();
              targetIndex = 0;
              break;
            case 'End':
              event.preventDefault();
              targetIndex = buttonsArray.length - 1;
              break;
          }

          if (targetIndex >= 0) {
            buttonsArray[targetIndex].focus();
          }
        });
      });
    }

    // Check URL for pre-filtered/paginated state
    handleInitialURL(filterButtons, insightItems, statusElement, paginationContainer);

    // Listen for hash changes (filter changes)
    window.addEventListener('hashchange', () => {
      currentPage = 1;
      handleInitialURL(filterButtons, insightItems, statusElement, paginationContainer);
    });

    // Initial render
    filteredItems = getFilteredItems(currentFilter, insightItems);
    renderPagination(paginationContainer, insightItems, statusElement);
    paginateInsights();
  }

  /**
   * Handle filter button click
   * @param {HTMLElement} clickedButton - The clicked filter button
   * @param {NodeListOf<HTMLElement>} allButtons - All filter buttons
   * @param {NodeListOf<HTMLElement>} insightItems - All insight items
   * @param {HTMLElement | null} statusElement - The status element for screen readers
   * @param {HTMLElement | null} paginationContainer - The pagination container
   */
  function handleFilterClick(clickedButton, allButtons, insightItems, statusElement, paginationContainer) {
    const filterValue = clickedButton.dataset.filter || 'all';
    currentFilter = filterValue;

    // Update active states on buttons
    allButtons.forEach((btn) => {
      btn.classList.remove(ACTIVE_CLASS);
      btn.setAttribute('aria-selected', 'false');
    });

    clickedButton.classList.add(ACTIVE_CLASS);
    clickedButton.setAttribute('aria-selected', 'true');

    // Get filtered items
    filteredItems = getFilteredItems(filterValue, insightItems);

    // Update URL
    updateURL(filterValue, currentPage);

    // Render pagination and paginate
    renderPagination(paginationContainer, insightItems, statusElement);
    paginateInsights();

    // Announce to screen readers
    announceResults(statusElement);
  }

  /**
   * Get items that match the current filter
   * @param {string} filterValue - The filter value ('all' or a service handle)
   * @param {NodeListOf<HTMLElement>} insightItems - All insight items
   * @returns {HTMLElement[]} Array of matching items
   */
  function getFilteredItems(filterValue, insightItems) {
    /** @type {HTMLElement[]} */
    const items = [];

    insightItems.forEach((item) => {
      const services = item.dataset.services || '';
      const servicesArray = services.split(',').map((s) => s.trim());
      const matchesFilter = filterValue === 'all' || servicesArray.includes(filterValue);

      // Reset filter hidden state
      item.classList.remove(HIDDEN_CLASS);
      item.removeAttribute('aria-hidden');

      if (matchesFilter) {
        items.push(item);
      } else {
        item.classList.add(HIDDEN_CLASS);
        item.setAttribute('aria-hidden', 'true');
      }
    });

    return items;
  }

  /**
   * Paginate the filtered items - show only items for current page
   */
  function paginateInsights() {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    filteredItems.forEach((item, index) => {
      if (index >= startIndex && index < endIndex) {
        item.classList.remove(PAGINATED_HIDDEN_CLASS);
      } else {
        item.classList.add(PAGINATED_HIDDEN_CLASS);
      }
    });
  }

  /**
   * Render pagination controls
   * @param {HTMLElement | null} container - The pagination container
   * @param {NodeListOf<HTMLElement>} insightItems - All insight items (for re-filtering on page change)
   * @param {HTMLElement | null} statusElement - The status element for screen readers
   */
  function renderPagination(container, insightItems, statusElement) {
    if (!container) return;

    const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

    // Don't show pagination if only 1 page or no items
    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    let html = '<ul class="insights-pagination__list" data-insights-pagination-list>';

    // Sliding indicator element
    html += '<li class="insights-pagination__indicator" data-insights-pagination-indicator aria-hidden="true"></li>';

    // Page numbers
    for (let i = 1; i <= totalPages; i++) {
      const isCurrentPage = i === currentPage;
      html += `
        <li class="insights-pagination__item">
          <button
            type="button"
            class="insights-pagination__button insights-pagination__button--number ${isCurrentPage ? PAGINATION_ACTIVE_CLASS : ''} button-unstyled single-line-small"
            data-insights-pagination-button
            ${isCurrentPage ? 'aria-current="page"' : ''}
            aria-label="${isCurrentPage ? `Page ${i}, current page` : `Page ${i}`}"
            data-page="${i}"
          >
            ${i}
          </button>
        </li>
      `;
    }

    html += '</ul>';
    container.innerHTML = html;

    // Get indicator and list elements
    const list = container.querySelector(PAGINATION_LIST_SELECTOR);
    const indicator = container.querySelector(PAGINATION_INDICATOR_SELECTOR);

    // Position indicator on active button initially
    requestAnimationFrame(() => {
      const activeButton = container.querySelector(`${PAGINATION_BUTTON_SELECTOR}[aria-current="page"]`);
      if (activeButton && indicator && list) {
        moveIndicator(activeButton, indicator, list);
      }
    });

    // Add click and hover listeners to pagination buttons
    /** @type {NodeListOf<HTMLButtonElement>} */
    const paginationButtons = container.querySelectorAll(PAGINATION_BUTTON_SELECTOR);
    paginationButtons.forEach((button) => {
      // Hover - move indicator to hovered button
      button.addEventListener('mouseenter', () => {
        if (!button.disabled && indicator && list) {
          moveIndicator(button, indicator, list);
        }
      });

      // Click handler
      button.addEventListener('click', () => {
        const pageAttr = button.dataset.page;
        if (!pageAttr) return;
        const page = parseInt(pageAttr, 10);
        if (page && page !== currentPage && page >= 1 && page <= totalPages) {
          currentPage = page;
          updateURL(currentFilter, currentPage);
          renderPagination(container, insightItems, statusElement);
          paginateInsights();
          announceResults(statusElement);

          // Scroll to top of insights section
          const insightsSection = document.querySelector(INSIGHTS_SECTION_SELECTOR);
          if (insightsSection) {
            insightsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    });

    // When mouse leaves the pagination, return indicator to active button
    if (list) {
      list.addEventListener('mouseleave', () => {
        const activeButton = container.querySelector(`${PAGINATION_BUTTON_SELECTOR}[aria-current="page"]`);
        if (activeButton && indicator) {
          moveIndicator(activeButton, indicator, list);
        }
      });
    }
  }

  /**
   * Move the sliding indicator to a target button
   * @param {Element} targetButton - The button to move indicator to
   * @param {Element} indicator - The indicator element
   * @param {Element} list - The pagination list element
   */
  function moveIndicator(targetButton, indicator, list) {
    const listRect = list.getBoundingClientRect();
    const buttonRect = targetButton.getBoundingClientRect();

    const left = buttonRect.left - listRect.left;
    const width = buttonRect.width;

    /** @type {HTMLElement} */ (list).style.setProperty('--indicator-left', `${left}px`);
    /** @type {HTMLElement} */ (list).style.setProperty('--indicator-width', `${width}px`);
  }

  /**
   * Update URL with current filter and page
   * @param {string} filterValue - The current filter value
   * @param {number} page - The current page number
   */
  function updateURL(filterValue, page) {
    const url = new URL(window.location.href);

    // Update or remove page parameter
    if (page > 1) {
      url.searchParams.set('page', page.toString());
    } else {
      url.searchParams.delete('page');
    }

    // Update hash for filter
    if (filterValue === 'all') {
      url.hash = '';
    } else {
      url.hash = filterValue;
    }

    history.replaceState(null, '', url.toString());
  }

  /**
   * Handle initial URL on page load
   * @param {NodeListOf<HTMLElement>} filterButtons - All filter buttons
   * @param {NodeListOf<HTMLElement>} insightItems - All insight items
   * @param {HTMLElement | null} statusElement - The status element for screen readers
   * @param {HTMLElement | null} paginationContainer - The pagination container
   */
  function handleInitialURL(filterButtons, insightItems, statusElement, paginationContainer) {
    const url = new URL(window.location.href);
    const hash = url.hash.slice(1);
    const pageParam = url.searchParams.get('page');

    // Set filter from hash
    if (hash) {
      currentFilter = hash;
      filterButtons.forEach((btn) => {
        if (btn.dataset.filter === hash) {
          filterButtons.forEach((b) => {
            b.classList.remove(ACTIVE_CLASS);
            b.setAttribute('aria-selected', 'false');
          });
          btn.classList.add(ACTIVE_CLASS);
          btn.setAttribute('aria-selected', 'true');
        }
      });
    }

    // Set page from param
    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (page && page >= 1) {
        currentPage = page;
      }
    }

    // Get filtered items and render
    filteredItems = getFilteredItems(currentFilter, insightItems);
    renderPagination(paginationContainer, insightItems, statusElement);
    paginateInsights();
  }

  /**
   * Announce results to screen readers
   * @param {HTMLElement | null} statusElement - The status element
   */
  function announceResults(statusElement) {
    if (!statusElement) return;

    const totalItems = filteredItems.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    const filterLabel = currentFilter === 'all' ? 'All categories' : currentFilter.replace(/-/g, ' ');

    let message;
    if (totalPages > 1) {
      message = `Showing page ${currentPage} of ${totalPages}. ${totalItems} ${totalItems === 1 ? 'insight' : 'insights'} for ${filterLabel}.`;
    } else {
      message = `${totalItems} ${totalItems === 1 ? 'insight' : 'insights'} for ${filterLabel}.`;
    }

    statusElement.textContent = message;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
